"use client";

import { COLOR, IllustrationCard } from "@/components/learn/chapter-1/_shared";
import { useDocLocale } from "@/components/shared/docs-blocks";
import type { Locale } from "@/lib/i18n";

const STRINGS: Record<
  Locale,
  {
    assets: Record<string, string>;
    segAmount: (amount: number) => string;
    reducesNav: string;
    beforeTotal: string;
    beforeLabel: string;
    beforeNav: string;
    afterTotal: string;
    afterLabel: string;
    afterNav: string;
    paidToInvestors: string;
    footerHeading: string;
    valueDown: string;
    cashUp: string;
    footerNote: string;
    title: string;
    description: string;
  }
> = {
  ja: {
    assets: { cash: "現金", bond: "債券", equity: "株式" },
    segAmount: (amount) => `${amount}億`,
    reducesNav: "分配すると、ファンドの純資産はその分だけ減る",
    beforeTotal: "110億円",
    beforeLabel: "分配前の純資産",
    beforeNav: "基準価額 11,000円",
    afterTotal: "105億円",
    afterLabel: "分配後の純資産",
    afterNav: "基準価額 10,500円",
    paidToInvestors: "分配で 5億円が投資家へ",
    footerHeading: "分配直後の見え方（税金・値動きの前）",
    valueDown: "評価額 −500円",
    cashUp: "現金 +500円",
    footerNote: "ここだけで損得は決まらない。税金、再投資、その後の値動きまで見る",
    title: "分配すると、純資産はその分だけ減る",
    description:
      "分配金は外から足されるお金ではなく、ファンドの中にあった資産の一部を投資家へ渡すものです。",
  },
  en: {
    assets: { cash: "Cash", bond: "Bonds", equity: "Equities" },
    segAmount: (amount) => `¥${(amount / 10).toFixed(1)}bn`,
    reducesNav: "A distribution reduces the fund's net assets by that amount",
    beforeTotal: "¥11.0bn",
    beforeLabel: "Net assets before",
    beforeNav: "NAV ¥11,000",
    afterTotal: "¥10.5bn",
    afterLabel: "Net assets after",
    afterNav: "NAV ¥10,500",
    paidToInvestors: "¥0.5bn goes to investors as a distribution",
    footerHeading: "How it looks right after the distribution (before tax and price moves)",
    valueDown: "Value −¥500",
    cashUp: "Cash +¥500",
    footerNote:
      "This alone does not decide gain or loss. Look at tax, reinvestment, and the price moves that follow",
    title: "A distribution reduces net assets by that amount",
    description:
      "A distribution is not money added from outside — it hands investors part of what was already inside the fund.",
  },
};

// ── Colors (株式=sky, 債券=amber, 現金=gray) ──────────────────────

const C = {
  equity: COLOR.sky,
  bond: COLOR.amber,
  cash: "#64748b",
  drop: COLOR.rose,
};

// ── Data ──────────────────────────────────────────────────────────
//   分配前 純資産 = 110億円（株式 55 + 債券 33 + 現金 22）
//   分配後 純資産 = 105億円（分配で 5億円が外へ）

type Segment = { key: string; amount: number; color: string };

const SEG_BEFORE: Segment[] = [
  { key: "cash", amount: 22, color: C.cash },
  { key: "bond", amount: 33, color: C.bond },
  { key: "equity", amount: 55, color: C.equity },
];

const SEG_AFTER: Segment[] = [
  { key: "cash", amount: 21, color: C.cash },
  { key: "bond", amount: 31.5, color: C.bond },
  { key: "equity", amount: 52.5, color: C.equity },
];

const NA_BEFORE = 110;
const NA_AFTER = 105;

// ── Layout ────────────────────────────────────────────────────────

const VW = 460;
const VH = 340;

const barW = 110;
const barGap = 90;
const leftX = (VW - (barW * 2 + barGap)) / 2;
const rightX = leftX + barW + barGap;

const baselineY = 230;
const topY = 55;
const chartH = baselineY - topY;
const valuePerPx = NA_BEFORE / chartH;

function barHeight(amount: number) {
  return amount / valuePerPx;
}

// ── Vertical segmented bar (stacked from bottom up) ──────────────

function VerticalSegmentedBar({ x, segments, showLabels }: { x: number; segments: Segment[]; showLabels: boolean }) {
  const t = STRINGS[useDocLocale()];
  return (
    <g>
      {segments.map((seg, index) => {
        const h = barHeight(seg.amount);
        const offsetFromBottom = segments.slice(0, index).reduce((sum, item) => sum + barHeight(item.amount), 0);
        const y = baselineY - offsetFromBottom - h;
        return (
          <g key={seg.key}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              fill={seg.color}
              fillOpacity={0.22}
              stroke={seg.color}
              strokeWidth="1"
              strokeOpacity={0.55}
            />
            {showLabels && h > 18 ? (
              <text
                x={x + barW / 2}
                y={y + h / 2 + 4}
                textAnchor="middle"
                fontSize="10"
                fill={seg.color}
                fontWeight="700"
              >
                {t.assets[seg.key]} {t.segAmount(seg.amount)}
              </text>
            ) : null}
          </g>
        );
      })}
    </g>
  );
}

// ── Illustration ──────────────────────────────────────────────────

function DistributionFlowIllustration() {
  const t = STRINGS[useDocLocale()];
  const beforeTotalH = barHeight(NA_BEFORE);
  const afterTotalH = barHeight(NA_AFTER);
  const dropH = beforeTotalH - afterTotalH;
  const beforeTopY = baselineY - beforeTotalH;
  const afterTopY = baselineY - afterTotalH;

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="mx-auto w-full max-w-xl">
      {/* Title */}
      <text x={VW / 2} y={20} textAnchor="middle" fontSize="11.5" fill="currentColor" fontWeight="700">
        {t.reducesNav}
      </text>

      {/* Reference line from top of before bar */}
      <line
        x1={leftX + barW}
        y1={beforeTopY}
        x2={rightX + barW + 70}
        y2={beforeTopY}
        stroke={C.drop}
        strokeWidth="0.8"
        strokeDasharray="3 3"
        strokeOpacity={0.5}
      />

      {/* Baseline */}
      <line
        x1={leftX - 10}
        y1={baselineY}
        x2={rightX + barW + 10}
        y2={baselineY}
        stroke="currentColor"
        strokeOpacity={0.25}
      />

      {/* Left bar: before distribution */}
      <VerticalSegmentedBar x={leftX} segments={SEG_BEFORE} showLabels={true} />
      <text
        x={leftX + barW / 2}
        y={beforeTopY - 8}
        textAnchor="middle"
        fontSize="11"
        fill="currentColor"
        fontWeight="800"
      >
        {t.beforeTotal}
      </text>
      <text
        x={leftX + barW / 2}
        y={baselineY + 16}
        textAnchor="middle"
        fontSize="9.5"
        fill="currentColor"
        fontWeight="700"
      >
        {t.beforeLabel}
      </text>
      <text
        x={leftX + barW / 2}
        y={baselineY + 32}
        textAnchor="middle"
        fontSize="8.5"
        fill="currentColor"
        fillOpacity={0.6}
      >
        {t.beforeNav}
      </text>

      {/* Right bar: after distribution */}
      <VerticalSegmentedBar x={rightX} segments={SEG_AFTER} showLabels={true} />

      {/* Ghost block on top of after bar = the amount that went out */}
      <rect
        x={rightX}
        y={afterTopY - dropH}
        width={barW}
        height={dropH}
        fill={C.drop}
        fillOpacity={0.08}
        stroke={C.drop}
        strokeWidth="1"
        strokeDasharray="3 2"
        strokeOpacity={0.55}
      />
      <text
        x={rightX + barW / 2}
        y={afterTopY - dropH - 8}
        textAnchor="middle"
        fontSize="11"
        fill={C.drop}
        fontWeight="800"
      >
        {t.afterTotal}
      </text>
      <text x={rightX + barW / 2} y={baselineY + 16} textAnchor="middle" fontSize="9.5" fill={C.drop} fontWeight="700">
        {t.afterLabel}
      </text>
      <text x={rightX + barW / 2} y={baselineY + 32} textAnchor="middle" fontSize="8.5" fill={C.drop} fillOpacity={0.7}>
        {t.afterNav}
      </text>

      {/* Arrow between bars */}
      <defs>
        <marker id="distArrow" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
          <path d="M 0 0 L 7 3.5 L 0 7 Z" fill={C.drop} fillOpacity={0.7} />
        </marker>
      </defs>
      <path
        d={`M ${leftX + barW + 10} ${beforeTopY + 20} Q ${(leftX + barW + rightX) / 2} ${beforeTopY - 18} ${rightX - 10} ${afterTopY - dropH / 2}`}
        fill="none"
        stroke={C.drop}
        strokeWidth="1.3"
        strokeOpacity={0.55}
        markerEnd="url(#distArrow)"
      />
      <text
        x={(leftX + barW + rightX) / 2}
        y={beforeTopY - 12}
        textAnchor="middle"
        fontSize="9.5"
        fill={C.drop}
        fontWeight="700"
      >
        {t.paidToInvestors}
      </text>

      {/* Investor's perspective footer (1口保有) */}
      {(() => {
        const footerY = 280;
        const footerH = 54;
        const footerX = 30;
        const footerW = VW - 60;
        return (
          <g>
            <rect
              x={footerX}
              y={footerY}
              width={footerW}
              height={footerH}
              rx={6}
              fill="currentColor"
              fillOpacity={0.04}
              stroke="currentColor"
              strokeOpacity={0.18}
            />
            <text
              x={VW / 2}
              y={footerY + 14}
              textAnchor="middle"
              fontSize="8.5"
              fill="currentColor"
              fillOpacity={0.65}
              fontWeight="700"
            >
              {t.footerHeading}
            </text>
            <text x={VW / 2} y={footerY + 32} textAnchor="middle" fontSize="10" fill="currentColor" fontWeight="600">
              <tspan fill={C.drop} fontWeight="700">
                {t.valueDown}
              </tspan>
              <tspan fillOpacity={0.55}> ＋ </tspan>
              <tspan fill={C.equity} fontWeight="700">
                {t.cashUp}
              </tspan>
            </text>
            <text x={VW / 2} y={footerY + 46} textAnchor="middle" fontSize="7.5" fill="currentColor" fillOpacity={0.5}>
              {t.footerNote}
            </text>
          </g>
        );
      })()}
    </svg>
  );
}

// ── Export ─────────────────────────────────────────────────────────

export function DocFundDistributionFlowDiagram() {
  const t = STRINGS[useDocLocale()];
  return (
    <IllustrationCard
      title={t.title}
      description={t.description}
    >
      <DistributionFlowIllustration />
    </IllustrationCard>
  );
}
